import type { GeneratedObjectRecord, RepoEntry } from '@/scenes/engine/utils/aiFashionSession'
import type { AIFashionImageResultRecord, AIFashionResultRecord } from '@/store/slices/aiFashion/types'

export function getAIFashionEntryDataUrl(entry: RepoEntry | null | undefined): string | null {
  return entry?.dataUrl ?? null
}

export function findAIFashionSourceImageResult(
  results: AIFashionResultRecord[],
  result: AIFashionResultRecord | null,
): AIFashionImageResultRecord | null {
  if (!result) return null
  if (result.kind !== 'video') return result
  const parent = results.find(candidate => candidate.objectId === result.sourceResultId)
  return parent && parent.kind !== 'video' ? parent : null
}

export type AIFashionSourceImageEntry = {
  sourceResultId: string
  imageResult: AIFashionImageResultRecord | null
  entry: RepoEntry
}

export function resolveAIFashionSourceImageEntry(
  results: AIFashionResultRecord[],
  result: AIFashionResultRecord | null,
  generatedObjectHistory?: Record<string, GeneratedObjectRecord>,
  productSource?: { productImageCacheKey: string | null; productRepoEntry: RepoEntry | null },
): AIFashionSourceImageEntry | null {
  if (!result) return null

  const sourceImageResult = findAIFashionSourceImageResult(results, result)
  if (sourceImageResult) {
    const entry = getActiveAIFashionResultEntry(sourceImageResult)
    return entry
      ? {
          sourceResultId: sourceImageResult.objectId,
          imageResult: sourceImageResult,
          entry,
        }
      : null
  }

  if (result.kind !== 'video') return null

  const historyEntry = generatedObjectHistory?.[result.sourceResultId]?.result
    ?? (result.sourceResultId === productSource?.productImageCacheKey ? productSource.productRepoEntry : null)
  return historyEntry
    ? {
        sourceResultId: result.sourceResultId,
        imageResult: null,
        entry: historyEntry,
      }
    : null
}

// Resolves the entry that reflects the user's currently selected quality for a result.
// Falls back to the standard `result` when no HD/4K variant has been generated yet.
// Returns null for video-kind results which have no image entry.
export function getActiveAIFashionResultEntry(
  result: AIFashionResultRecord,
): RepoEntry | null {
  if (result.kind === 'video') return null
  const activeQuality = result.activeQuality
  if (activeQuality && activeQuality !== 'standard') {
    const cached = result.qualityResults?.[activeQuality]
    if (cached) return cached
  }
  return result.result
}

export function getAIFashionResultDataUrl(
  result: AIFashionResultRecord,
): string | null {
  return getAIFashionEntryDataUrl(getActiveAIFashionResultEntry(result))
}

export function getGeneratedAIFashionResultDataUrls(
  results: AIFashionResultRecord[],
): string[] {
  const dataUrls = results
    .filter(result => result.kind === 'generated')
    .map(result => getAIFashionResultDataUrl(result))
  return dataUrls.filter((dataUrl): dataUrl is string => Boolean(dataUrl))
}
